import type { HTMLAttributes } from 'react'
import './Skeleton.scss'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Shape preset — controls only the corner radius. Width and height come from
   * `className` (e.g. Tailwind `h-9 w-[240px]` for an input, `size-20` for an
   * avatar), so the skeleton can be sized to exactly match the element it
   * stands in for.
   * @default 'line'
   */
  variant?: 'line' | 'box' | 'avatar'
}

/**
 * Pulsing placeholder shown while async content is still loading, so rows keep
 * their height and nothing pops in / shifts layout once the data arrives.
 *
 * `aria-hidden` because a skeleton carries no information — screen readers
 * should announce the resolved content, not the placeholder.
 *
 * @example
 * // Input-sized line (36px tall, matches the shared Input component)
 * <Skeleton variant="box" className="h-9 w-[240px]" />
 *
 * @example
 * // 80px squircle avatar placeholder
 * <Skeleton variant="avatar" className="size-20 shrink-0" />
 */
export function Skeleton({ variant = 'line', className = '', ...props }: SkeletonProps) {
  return (
    <div
      className={`skeleton skeleton--${variant} ${className}`.trim()}
      aria-hidden="true"
      {...props}
    />
  )
}
